import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { I18nService } from '../i18n/i18n.service';

/**
 * Tytuł karty w przeglądarce. Składa się z trzech części, od najbardziej
 * szczegółowej: nazwa podstrony, nazwa drzewa, nazwa serwisu - np.
 * „Kwiatkowski · Moja Genealogia · Genealogy Cards". Najważniejsze idzie
 * pierwsze, bo zakładki obcinają koniec.
 *
 * Nazwa podstrony bierze się z `data.title` trasy (klucz tłumaczenia); parametry
 * trasy są dostępne w tekście jako {nazwa}, więc np. wyszukiwanie po nazwisku
 * może pokazać samo nazwisko. Strony pokazujące konkretny obiekt (profil osoby)
 * podają swój tytuł same przez setDetail().
 */
@Injectable()
export class PageTitleService {

    /** Nazwa serwisu - zawsze na końcu tytułu. */
    private readonly appName = 'Genealogy Cards';

    /** Tytuł ustawiony przez komponent (np. imię i nazwisko z profilu). */
    private detail?: string;

    /** Klucz i parametry z aktualnej trasy - przeliczane po zmianie języka. */
    private routeKey?: string;
    private routeParams: Record<string, string> = {};

    constructor(private router: Router, private title: Title, private i18n: I18nService) { }

    /** Wołane raz przy starcie aplikacji. */
    start(): void {
        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe(() => {
                // nowa trasa unieważnia tytuł ustawiony przez poprzedni komponent
                this.detail = undefined;
                this.readRoute();
                this.apply();
            });

        // zmiana języka bez przeładowania strony musi odświeżyć też tytuł
        this.i18n.languageChanged.subscribe(() => this.apply());
    }

    /**
     * Tytuł podany przez komponent - dla stron opisujących konkretny obiekt,
     * którego nazwy trasa nie zna (profil osoby, gałąź, miejsce).
     * Pusty tekst przywraca tytuł z trasy.
     */
    setDetail(text: string | undefined): void {
        this.detail = text?.trim() || undefined;
        this.apply();
    }

    /** Najgłębsza trasa z `data.title` wygrywa - tak jak przy wyborze układu. */
    private readRoute(): void {
        let node: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
        let key: string | undefined;
        let params: Record<string, string> = {};

        while (node) {
            const dataTitle = node.data?.['title'] as string | undefined;
            if (dataTitle) {
                key = dataTitle;
                params = { ...params, ...node.params };
            }
            node = node.firstChild;
        }
        this.routeKey = key;
        this.routeParams = params;
    }

    private apply(): void {
        const strona = this.detail ?? (this.routeKey ? this.i18n.t(this.routeKey, this.routeParams) : undefined);
        const drzewo = environment.currentTreeDescription || undefined;

        // drzewo tylko wtedy, gdy jesteśmy w jego obrębie i nie jest już tytułem
        const wDrzewie = this.router.url.startsWith('/tree/');
        const czesci = [strona, wDrzewie && drzewo !== strona ? drzewo : undefined, this.appName]
            .filter((czesc): czesc is string => !!czesc);

        this.title.setTitle(czesci.join(' · '));
    }
}
